import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MatterType } from '@/services/types/client-intake-types';
import type { QualificationAction } from '@/services/types/firms-types';

interface CaseTypeActionsConfigProps {
  actions: Record<string, QualificationAction>;
  defaultAction: QualificationAction;
  onChange: (
    actions: Record<string, QualificationAction>,
    defaultAction: QualificationAction,
  ) => void;
  disabled?: boolean;
}

const CASE_TYPES = Object.values(MatterType);

const ACTION_CONFIG: Record<
  QualificationAction,
  { label: string; color: string }
> = {
  accept: {
    label: 'Accept',
    color: 'text-green-700 bg-green-50 border-green-200',
  },
  reject: { label: 'Reject', color: 'text-red-700 bg-red-50 border-red-200' },
  escalate: {
    label: 'Escalate',
    color: 'text-amber-700 bg-amber-50 border-amber-200',
  },
};

export const CaseTypeActionsConfig = ({
  actions,
  defaultAction,
  onChange,
  disabled,
}: CaseTypeActionsConfigProps) => {
  const getAction = (type: string): QualificationAction =>
    actions[type] ?? defaultAction;

  const setAction = (type: string, action: QualificationAction | 'default') => {
    if (action === 'default') {
      const { [type]: _, ...rest } = actions;
      onChange(rest, defaultAction);
    } else {
      onChange({ ...actions, [type]: action }, defaultAction);
    }
  };

  // Shorten display names for compactness
  const getShortName = (type: string) => {
    const shortNames: Record<string, string> = {
      'Motor Vehicle Accident': 'MVA',
      'Medical Malpractice': 'Med Mal',
      'Premises Liability': 'Premises',
      'Breach of Employment Contract': 'Employment',
      'Seller Non-Disclosure': 'Non-Disclosure',
      'Broker Negligence': 'Broker Neg.',
      Miscellaneous: 'Misc',
      Unknown: 'Unknown',
    };
    return shortNames[type] || type;
  };

  const customCount = Object.keys(actions).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Case Type Actions</span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Default:</span>
          <Select
            value={defaultAction}
            onValueChange={(v) => onChange(actions, v as QualificationAction)}
            disabled={disabled}
          >
            <SelectTrigger
              className={`h-7 w-24 text-xs ${ACTION_CONFIG[defaultAction].color}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="accept">Accept</SelectItem>
              <SelectItem value="reject">Reject</SelectItem>
              <SelectItem value="escalate">Escalate</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Three column grid */}
      <div className="grid grid-cols-3 gap-1.5">
        {CASE_TYPES.map((type) => {
          const currentAction = getAction(type);
          const isCustom = type in actions;
          const config = ACTION_CONFIG[currentAction];

          return (
            <div
              key={type}
              className={`flex items-center justify-between gap-2 rounded-md border px-2 py-1.5 ${
                isCustom
                  ? 'border-blue-200 bg-blue-50/40'
                  : 'border-gray-100 bg-gray-50/50'
              }`}
            >
              <span
                className="min-w-0 flex-1 truncate text-xs text-gray-700"
                title={type}
              >
                {getShortName(type)}
              </span>
              <Select
                value={isCustom ? currentAction : 'default'}
                onValueChange={(v) =>
                  setAction(type, v as QualificationAction | 'default')
                }
                disabled={disabled}
              >
                <SelectTrigger
                  className={`h-6 w-20 shrink-0 border text-xs ${
                    isCustom
                      ? config.color
                      : 'border-gray-200 bg-white text-gray-500'
                  }`}
                >
                  <SelectValue>
                    {isCustom ? config.label : 'Default'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">
                    <span className="text-gray-500">Default</span>
                  </SelectItem>
                  <SelectItem value="accept">
                    <span className="text-green-700">Accept</span>
                  </SelectItem>
                  <SelectItem value="reject">
                    <span className="text-red-700">Reject</span>
                  </SelectItem>
                  <SelectItem value="escalate">
                    <span className="text-amber-700">Escalate</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground">
        {customCount === 0
          ? `All case types use default action (${ACTION_CONFIG[defaultAction].label})`
          : `${customCount} custom rule${customCount > 1 ? 's' : ''}, ${CASE_TYPES.length - customCount} use default`}
      </p>
    </div>
  );
};
